"use client";

import React, { useState, useCallback } from "react";
import { Box, Typography, TextField, Rating, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import ReviewWritingAssistant from "@/app/components/ai/ReviewWritingAssistant";
import { useToast } from "@/utils/theme/useToast";

const ReviewSubmissionModal = ({
  open,
  onClose,
  sharerName,
  sharerId,
  meetingId,
  onReviewSubmitted,
  meetingSummary = null,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState("");
  const [privateFeedback, setPrivateFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const ratingLabels = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent",
  };

  const handleSubmit = useCallback(async () => {
    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/reviews", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sharer_id: sharerId,
          meeting_id: meetingId,
          rating: rating,
          comment: review.trim(),
          private_feedback: privateFeedback.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit review");
      }

      if (onReviewSubmitted) {
        onReviewSubmitted();
      } else {
        handleClose();
      }

      toast.show(
        "Review Submitted",
        "Your review has been submitted successfully.",
      );
    } catch (err) {
      setError(err.message || "Failed to submit review. Please try again.");
      console.error("Review submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [rating, review, privateFeedback, sharerId, meetingId, onReviewSubmitted]);
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setRating(0);
      setReview("");
      setPrivateFeedback("");
      setError("");
      onClose();
    }
  }, [isSubmitting, onClose]);

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title="Review your meeting"
      subtitle={`with ${sharerName}`}
      showIgnoreButton={false}
      showConfirmButton={true}
      confirmButtonText={isSubmitting ? "Submitting..." : "Submit Review"}
      ignoreButtonText="Cancel"
      showCloseIcon={true}
      isProcessing={isSubmitting}
      processingText="Submitting..."
      disableConfirm={rating === 0}
      // confirmButtonVariant="contained"
      modalStyle="center"
      backdropStyle="modern"
      body={
        <Box
          sx={{
            textAlign: "left",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            maxHeight: { xs: "55vh", sm: "unset" },
            overflowY: { xs: "auto", sm: "visible" },
            pr: { xs: 1, sm: 0 },
            pb: { xs: 2, sm: 0 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Rating */}
          <Box sx={{ mb: 4, borderRadius: "0px !important" }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Rate this meeting
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Rating
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue || 0);
                  setError("");
                }}
                onChangeActive={(event, newHover) => {
                  setHoverRating(newHover);
                }}
                size="large"
                icon={<StarIcon sx={{ fontSize: "2rem" }} />}
                emptyIcon={<StarBorderIcon sx={{ fontSize: "2rem" }} />}
                sx={{
                  "& .MuiRating-iconFilled": {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : theme.palette.grey[800],
                  },
                  "& .MuiRating-iconHover": {
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.light
                        : theme.palette.grey[600],
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  minWidth: 80,
                }}
              >
                {hoverRating > 0
                  ? ratingLabels[hoverRating]
                  : rating > 0
                    ? ratingLabels[rating]
                    : "Select rating"}
              </Typography>
            </Box>
          </Box>

          {/* Public Review */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 400 }}>
                Share your experience
              </Typography>
              <ReviewWritingAssistant
                currentReview={review}
                onUseContent={(content) => setReview(content)}
                rating={rating}
                sharerName={sharerName}
                meetingSummary={meetingSummary}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={
                rating === 0
                  ? "Please select a rating first"
                  : "What did you appreciate about this meeting?"
              }
              value={review}
              onChange={(e) => setReview(e.target.value)}
              disabled={isSubmitting || rating === 0}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              helperText={`${review.length}/500 characters`}
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                  opacity: 0.7,
                },
              }}
            />
          </Box>

          {/* Private Feedback */}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 400, mb: 1 }}>
              Visible to admins only
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder={
                rating === 0
                  ? "Please select a rating first"
                  : "Any feedback you'd like to share privately?"
              }
              value={privateFeedback}
              onChange={(e) => setPrivateFeedback(e.target.value)}
              disabled={isSubmitting || rating === 0}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
              helperText={`${privateFeedback.length}/500 characters`}
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "0.875rem",
                  opacity: 0.7,
                },
              }}
            />
          </Box>
        </Box>
      }
      minHeight="auto"
      mobileMinHeight="auto"
    />
  );
};

export default ReviewSubmissionModal;
